import { apiClient } from './apiClient';
import { LlmProvider } from '@/types/llm-provider';

export interface LlmResponse {
  llm: LlmProvider;
  responseText: string;
  mentioned: boolean;
  citations: Array<{ url: string; title: string; text: string }>;
}

export interface QueryTestResult {
  id: string;
  query: string;
  response: string;
  timestamp: string;
  mentionsWebsite: boolean;
  llmResults: LlmResponse[];
}

export interface DashboardMetrics {
  queriesTested: number;
  visibilityScore: number;
  visibilityTrend: 'up' | 'down' | 'neutral';
  mentionPercentage: number;
  mentionTrend: 'up' | 'down' | 'neutral';
  noMentionPercentage: number;
  noMentionTrend: 'up' | 'down' | 'neutral';
}

export interface MentionTimelinePoint {
  date: string;
  mentions: number;
  noMentions: number;
  total: number;
}

export interface DashboardData {
  metrics: DashboardMetrics;
  recentQueries: QueryTestResult[];
  mentionsTimeline: MentionTimelinePoint[];
  period: string;
  isSubscribed: boolean;
}

export interface TestQueriesRequest {
  queries: string[];
  targetDomain: string;
  resourceId?: string;
}

export interface ProviderTimelinePoint {
  date: string;
  provider: LlmProvider;
  mentions: number;
  total: number;
  mentionRate: number;
}

export interface ProviderSummary {
  provider: LlmProvider;
  totalQueries: number;
  mentions: number;
  mentionRate: number;
  trend: 'up' | 'down' | 'neutral';
  trendPercentage: number;
}

export interface ProviderAnalyticsData {
  timeline: ProviderTimelinePoint[];
  providerSummaries: ProviderSummary[];
}

const transformQueryResult = (result: any): QueryTestResult => ({
  id: result.id,
  query: result.query,
  response: result.llmResponses?.[0]?.responseText || 'No response available',
  timestamp: result.testedAt,
  mentionsWebsite: result.mentionsWebsite || result.llmResponses?.some((r: any) => r.mentioned) || false,
  llmResults: (result.llmResponses || []).map((llmResp: any) => ({
    llm: llmResp.llmProvider as LlmProvider,
    responseText: llmResp.responseText || '',
    mentioned: llmResp.mentioned,
    citations: llmResp.citations || [],
  })),
});

// Transform for analytics dashboard endpoint (different structure from query endpoint)
const transformDashboardQuery = (result: any): QueryTestResult => ({
  id: result.id,
  query: result.query,
  response: result.response || 'No response available',
  timestamp: result.timestamp,
  mentionsWebsite: result.llmResults?.some((r: any) => r.mentioned) || false,
  llmResults: (result.llmResults || []).map((llmResult: any) => ({
    llm: llmResult.llm as LlmProvider,
    responseText: llmResult.responseText || '',
    mentioned: llmResult.mentioned,
    citations: llmResult.citationUrl ? [{ url: llmResult.citationUrl, title: '', text: '' }] : [],
  })),
});

export const queriesService = {
  testQueries: async (request: TestQueriesRequest): Promise<QueryTestResult[]> => {
    const response = await apiClient.post('/queries/test', request, { timeout: 180000 });
    return response.data.map(transformQueryResult);
  },

  getQueriesByResource: async (resourceId: string): Promise<QueryTestResult[]> => {
    const response = await apiClient.get(`/queries/by-resource/${resourceId}`);
    return response.data.map(transformQueryResult);
  },

  getDashboardData: async (start: string, end: string, resourceId?: string): Promise<DashboardData> => {
    const params: Record<string, string> = { start, end };
    if (resourceId) {
      params.resourceId = resourceId;
    }
    const response = await apiClient.get(`/analytics/dashboard`, { params });
    const data = response.data;
    return {
      ...data,
      recentQueries: (data.recentQueries || []).map(transformDashboardQuery),
      mentionsTimeline: data.mentionsTimeline || [],
    };
  },

  getQueryResults: async (limit: number = 10, offset: number = 0, resourceId?: string): Promise<QueryTestResult[]> => {
    const params: Record<string, any> = { limit, offset };
    if (resourceId) {
      params.resourceId = resourceId;
    }
    const response = await apiClient.get('/queries', { params });
    const results = response.data?.results || [];
    return results.map(transformQueryResult);
  },

  getProviderAnalytics: async (start: string, end: string, resourceId?: string): Promise<ProviderAnalyticsData> => {
    const params: Record<string, string> = { start, end };
    if (resourceId) {
      params.resourceId = resourceId;
    }
    const response = await apiClient.get('/analytics/provider-analytics', { params });
    return response.data;
  },
};
